import styled from "styled-components";
import { largeComponentHeight } from "common/const/const";
import React, { useLayoutEffect, useRef, useState } from "react";

import {
  inputBorderStyle,
  InputWrapper,
} from "common/components/Wrapper/InputBase";
import { useValidCheckSingle } from "common/hooks/validCheck";

export const StyledInputBase = styled.input`
  ${inputBorderStyle}
  align-items: center;
  width: 100%;
  height: ${largeComponentHeight};
  padding: 0;
  padding-left: ${(props) =>
    props.$hasPrefix
      ? "2rem"
      : props.iconPosition === "left"
        ? "3.5rem"
        : "1.25rem"};
  padding-right: ${(props) =>
    props.$hasSuffix
      ? "2rem"
      : props.iconPosition === "right"
        ? "3.5rem"
        : "1.25rem"};

  font-family: var(--font-family);
  font-size: var(--fontSize-14);

  box-sizing: border-box;

  ${({ $borderless, $error }) => $borderless && !$error && `border: none;`}
`;

export const TextInput = ({
  //InputWrapper
  label,
  caption,
  icon,
  rules,

  //TextInput
  name,
  type = "text",
  pattern,
  placeholder,
  disabled,
  readOnly,
  defaultValue,
  onChange,
  forceError,
  prefix,
  suffix,
  displayFormat,
  parseValue,
  borderless,
  ...rest
}) => {
  const prefixRef = useRef(null);
  const [prefixWidth, setPrefixWidth] = useState(0);

  const suffixRef = useRef(null);
  const [suffixWidth, setSuffixWidth] = useState(0);

  const [isComposing, setIsComposing] = useState(false);
  const lastValidValueRef = useRef("");
  const inputRef = useRef(null);
  const pendingCursorRef = useRef(null);

  const { inputProps, error, onValueChange } = useValidCheckSingle({
    name: name,
    rules: rules,
    defaultValue: defaultValue,
  });

  const hasError = forceError || error;

  const rawValue = inputProps.value ?? "";

  let displayValue = rawValue;
  if (disabled) {
    displayValue = displayFormat ? displayFormat(rawValue) : "";
  } else if (
    displayFormat &&
    rawValue !== "" &&
    rawValue !== null &&
    rawValue !== undefined &&
    !isComposing
  ) {
    displayValue = displayFormat(rawValue);
  }

  useLayoutEffect(() => {
    if (prefixRef.current) {
      setPrefixWidth(prefixRef.current.offsetWidth);
    }
  }, [prefix]);

  useLayoutEffect(() => {
    if (suffixRef.current) {
      setSuffixWidth(suffixRef.current.offsetWidth);
    }
  }, [suffix]);

  // 렌더링 커밋 직후 커서 복원
  useLayoutEffect(() => {
    if (pendingCursorRef.current !== null && inputRef.current) {
      const pos = pendingCursorRef.current;
      inputRef.current.setSelectionRange(pos, pos);
      pendingCursorRef.current = null;
    }
  });

  const setRef = (el) => {
    inputRef.current = el;
    if (typeof inputProps.ref === "function") {
      inputProps.ref(el);
    } else if (inputProps.ref) {
      inputProps.ref.current = el;
    }
  };

  return (
    <InputWrapper
      label={label}
      caption={caption}
      icon={icon}
      rules={rules}
      error={hasError}>
      <InputRow>
        <Prefix ref={prefixRef}>{prefix}</Prefix>
        <StyledInputBase
          style={{
            ...(prefix ? { paddingLeft: `${prefixWidth + 24}px` } : {}),
            ...(suffix ? { paddingRight: `${suffixWidth + 24}px` } : {}),
          }}
          {...(type === "file"
            ? { ref: setRef, onBlur: inputProps.onBlur }
            : { ...inputProps, ref: setRef })}
          type={type}
          name={name}
          onCompositionStart={() => {
            setIsComposing(true);
            lastValidValueRef.current = inputProps.value ?? "";
          }}
          onCompositionEnd={(e) => {
            setIsComposing(false);

            const value = e.target.value;
            const rawValue = parseValue ? parseValue(value) : value;

            if (pattern && !pattern.test(rawValue)) {
              // pattern 위반 → 직전 유효값으로 복원
              e.target.value = lastValidValueRef.current;
              onValueChange(lastValidValueRef.current);
            } else {
              // 통과 → 유효값 갱신
              lastValidValueRef.current = rawValue;
              onValueChange(rawValue);
            }
          }}
          onChange={(e) => {
            if (type === "file") {
              const file = e.target.files?.[0] || null;
              onChange?.(file);
              return;
            }

            const input = e.target;
            const newDisplayValue = input.value;
            const cursorPos = input.selectionStart;

            // parseValue가 있으면 raw 추출, 없으면 그대로
            const nextRawValue = parseValue
              ? parseValue(newDisplayValue)
              : newDisplayValue;

            if (isComposing || !pattern || pattern.test(nextRawValue)) {
              // displayFormat이 있을 때 커서 위치 보정
              if (displayFormat && parseValue && !isComposing) {
                // 커서 앞에서 raw 문자 개수
                const rawCharsBeforeCursor = parseValue(
                  newDisplayValue.slice(0, cursorPos),
                ).length;

                // 포맷된 문자열에서 raw 문자 개수만큼 지난 위치 계산
                const formatted = displayFormat(nextRawValue);
                let newPos = 0;
                let count = 0;
                for (let i = 0; i < formatted.length; i++) {
                  if (count >= rawCharsBeforeCursor) break;
                  // raw에 포함되는 문자인지 검사
                  if (parseValue(formatted[i]).length > 0) count++;
                  newPos = i + 1;
                }
                pendingCursorRef.current = newPos;
              }

              if (!isComposing) lastValidValueRef.current = nextRawValue;
              onValueChange(nextRawValue);
              onChange?.(nextRawValue);
            }
          }}
          placeholder={
            type === "file"
              ? undefined
              : readOnly
                ? undefined
                : disabled
                  ? (inputProps.value ?? placeholder)
                  : placeholder
          }
          {...(type !== "file" && { value: displayValue })}
          onFocus={(e) => {
            if (displayFormat) e.target.select();
            inputProps.onFocus?.(e);
          }}
          disabled={disabled}
          readOnly={readOnly}
          iconPosition={icon?.position}
          $error={!!hasError}
          $borderless={borderless}
          $hasPrefix={!!prefix}
          $hasSuffix={!!suffix}
          pattern={type === "file" ? undefined : pattern?.source}
          {...rest}
        />
        <Suffix ref={suffixRef}>{suffix}</Suffix>
        {icon?.element && icon.position?.toLowerCase() === "right" && (
          <IconContainer $position="right">{icon.element}</IconContainer>
        )}
        {icon?.element && icon.position?.toLowerCase() === "left" && (
          <IconContainer $position="left">{icon.element}</IconContainer>
        )}
      </InputRow>
    </InputWrapper>
  );
};

const InputRow = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
`;

const Prefix = styled.span`
  position: absolute;
  left: 1.25rem;
  font-family: var(--font-family);
  font-size: var(--fontSize-14);
  pointer-events: none;
`;

const Suffix = styled.span`
  position: absolute;
  right: 1.25rem;
  font-family: var(--font-family);
  font-size: var(--fontSize-14);
  pointer-events: none;
`;

const IconContainer = styled.div`
  position: absolute;
  ${(props) => props.$position}: 1rem;
  display: flex;
  align-items: center;
  height: 100%;
  top: 0;
`;
